import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Trash2, CheckCircle2, AlertCircle, X, Loader,
  Shield, Phone, Key, Edit2, Save, TestTube, ExternalLink,
} from 'lucide-react';
import api from '../../services/api';

const EMPTY_FORM = { nome: '', phone_number_id: '', waba_id: '', access_token: '', phone_display: '' };

function NumeroCard({ numero, onDelete, onTest }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...numero, access_token: '' });
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/whatsapp-oficial/${numero.id}`, form);
      qc.invalidateQueries(['wa-oficial-numeros']);
      setEditing(false);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-3">
      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 font-medium">Nome</label>
              <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Número exibido</label>
              <input value={form.phone_display} onChange={e => setForm(f => ({ ...f, phone_display: e.target.value }))} placeholder="Ex: +55 44 99999-9999" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Phone Number ID</label>
              <input value={form.phone_number_id} onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">WABA ID</label>
              <input value={form.waba_id} onChange={e => setForm(f => ({ ...f, waba_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">Access Token (deixe em branco para manter o atual)</label>
            <input type="password" value={form.access_token} onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))} placeholder="EAABs..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
              <Save size={12} /> {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs hover:bg-gray-200">Cancelar</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Shield size={18} className="text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-800 text-sm">{numero.nome}</div>
                {numero.phone_display && <div className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} />{numero.phone_display}</div>}
              </div>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">API Oficial</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <div className="text-gray-400 uppercase tracking-wide mb-0.5">Phone Number ID</div>
              <div className="font-mono text-gray-700 truncate">{numero.phone_number_id}</div>
            </div>
            {numero.waba_id && (
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-gray-400 uppercase tracking-wide mb-0.5">WABA ID</div>
                <div className="font-mono text-gray-700 truncate">{numero.waba_id}</div>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => onTest(numero.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100">
              <TestTube size={12} /> Testar Conexão
            </button>
            <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">
              <Edit2 size={12} /> Editar
            </button>
            <button onClick={() => onDelete(numero.id, numero.nome)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 ml-auto">
              <Trash2 size={12} /> Remover
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function WhatsAppOficial() {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['wa-oficial-numeros'],
    queryFn: () => api.get('/whatsapp-oficial').then(r => r.data),
  });

  const numeros = data?.data || [];

  const handleCriar = async () => {
    setErro('');
    if (!form.nome || !form.phone_number_id || !form.access_token) {
      return setErro('Nome, Phone Number ID e Access Token são obrigatórios');
    }
    setCreating(true);
    try {
      await api.post('/whatsapp-oficial', form);
      qc.invalidateQueries(['wa-oficial-numeros']);
      setSucesso(`Número "${form.nome}" adicionado com sucesso!`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      setTimeout(() => setSucesso(''), 4000);
    } catch (err) {
      setErro(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!confirm(`Remover "${nome}"?`)) return;
    try {
      await api.delete(`/whatsapp-oficial/${id}`);
      qc.invalidateQueries(['wa-oficial-numeros']);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleTest = async (id) => {
    setTestLoading(id);
    setTestResult(null);
    try {
      const res = await api.post(`/whatsapp-oficial/${id}/testar`);
      setTestResult({ ok: true, data: res.data.data });
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestLoading(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">API Oficial Meta (WhatsApp)</h1>
          <p className="text-sm text-gray-500 mt-0.5">Conecte números via WhatsApp Business Cloud API oficial da Meta</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-secondary">
          <Plus size={15} /> Adicionar Número
        </button>
      </div>

      {erro && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <AlertCircle size={15} /> {erro}
          <button onClick={() => setErro('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      {sucesso && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl">
          <CheckCircle2 size={15} /> {sucesso}
        </div>
      )}
      {testResult && (
        <div className={`flex items-start gap-2 text-sm px-4 py-3 rounded-xl border ${testResult.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {testResult.ok ? <CheckCircle2 size={15} className="mt-0.5" /> : <AlertCircle size={15} className="mt-0.5" />}
          {testResult.ok
            ? <span>Conexão OK — <strong>{testResult.data?.verified_name}</strong> ({testResult.data?.display_phone_number}) — Qualidade: {testResult.data?.quality_rating}</span>
            : <span>{testResult.error}</span>
          }
          <button onClick={() => setTestResult(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-xl shadow p-5 space-y-4 border-2 border-blue-100">
          <h2 className="font-semibold text-gray-700 text-sm flex items-center gap-2"><Key size={15} /> Novo Número — API Oficial Meta</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome *</label>
              <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Vendas Inbase" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Número (exibição)</label>
              <input value={form.phone_display} onChange={e => setForm(f => ({ ...f, phone_display: e.target.value }))} placeholder="Ex: +55 44 3030-0000" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Phone Number ID *</label>
              <input value={form.phone_number_id} onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))} placeholder="123456789012345" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">WABA ID (WhatsApp Business Account)</label>
              <input value={form.waba_id} onChange={e => setForm(f => ({ ...f, waba_id: e.target.value }))} placeholder="987654321098765" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Access Token (System User Token) *</label>
            <input type="password" value={form.access_token} onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))} placeholder="EAABs... (token permanente do usuário do sistema)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <p className="text-xs text-gray-400 mt-1">Gere em: developers.facebook.com → Seu App → WhatsApp → API Setup → System User Access Token</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleCriar} disabled={creating} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-secondary disabled:opacity-50">
              <Plus size={15} /> {creating ? 'Salvando...' : 'Salvar Número'}
            </button>
            <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-10 text-center text-gray-400 flex items-center justify-center gap-2"><Loader size={16} className="animate-spin" /> Carregando...</div>
      ) : numeros.length === 0 ? (
        <div className="bg-white rounded-xl shadow py-14 text-center text-gray-400 space-y-2">
          <Shield size={32} className="mx-auto opacity-30" />
          <p className="text-sm">Nenhum número da API Oficial configurado.</p>
          <p className="text-xs">Clique em "Adicionar Número" para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {numeros.map(n => (
            <NumeroCard
              key={n.id}
              numero={n}
              onDelete={handleDelete}
              onTest={handleTest}
            />
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700 space-y-1">
        <p className="font-semibold">Como obter as credenciais da API Oficial Meta:</p>
        <p>1. Acesse <strong>developers.facebook.com</strong> e crie um App (tipo: Business)</p>
        <p>2. Adicione o produto <strong>WhatsApp</strong> ao app e configure um número</p>
        <p>3. Em WhatsApp → API Setup, copie o <strong>Phone Number ID</strong> e o <strong>WhatsApp Business Account ID</strong></p>
        <p>4. Crie um <strong>System User</strong> permanente e gere o access token com permissão <code>whatsapp_business_messaging</code></p>
        <p>5. Cole as credenciais acima e clique em "Testar Conexão" para verificar</p>
        <div className="flex items-center gap-1 pt-1">
          <ExternalLink size={11} />
          <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noreferrer" className="underline">Documentação oficial da Cloud API</a>
        </div>
      </div>
    </div>
  );
}
